import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Loader2, Search } from 'lucide-react';
import { useProjects } from '@/hooks/useProjects';
import { useAddJobToProject } from '@/hooks/useProjects';

interface LinkJobToProjectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobId: string;
  alreadyLinkedProjectIds: string[];
}

export function LinkJobToProjectDialog({
  open,
  onOpenChange,
  jobId,
  alreadyLinkedProjectIds,
}: LinkJobToProjectDialogProps) {
  const [search, setSearch] = useState('');
  const { data: projects, isLoading } = useProjects();
  const addJob = useAddJobToProject();

  const filtered = projects?.filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase())
  ) ?? [];

  const handleLink = (projectId: string) => {
    addJob.mutate(
      { projectId, jobId },
      { onSuccess: () => onOpenChange(false) }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add to Existing Project</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 pt-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              className="pl-9"
              placeholder="Search projects..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              autoFocus
            />
          </div>

          <div className="max-h-64 overflow-y-auto space-y-1">
            {isLoading ? (
              <div className="flex justify-center py-6">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : filtered.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">No projects found</p>
            ) : (
              filtered.map(project => {
                const alreadyLinked = alreadyLinkedProjectIds.includes(project.id);
                return (
                  <div
                    key={project.id}
                    className="flex items-center justify-between rounded-lg border px-3 py-2.5"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{project.name}</p>
                      {project.current_status && (
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <div
                            className="h-2 w-2 rounded-full"
                            style={{ backgroundColor: project.current_status.color }}
                          />
                          <p className="text-xs text-muted-foreground">{project.current_status.name}</p>
                        </div>
                      )}
                    </div>
                    <Button
                      size="sm"
                      variant={alreadyLinked ? 'secondary' : 'default'}
                      className="h-7 ml-2 flex-shrink-0"
                      disabled={alreadyLinked || addJob.isPending}
                      onClick={() => handleLink(project.id)}
                    >
                      {alreadyLinked ? 'Linked' : 'Add'}
                    </Button>
                  </div>
                );
              })
            )}
          </div>

          <div className="flex justify-end pt-1">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Done</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
